import React, { useEffect, useState } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  makeStyles,
  Switch,
  TextField,
} from "@material-ui/core";
import { Colorize } from "@material-ui/icons";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import ColorPicker from "../ColorPicker";

const useStyles = makeStyles((theme) => ({
  row: {
    display: "flex",
    gap: theme.spacing(1),
  },
  colorAdornment: {
    width: 20,
    height: 20,
    borderRadius: theme.borderRadius.small,
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const TagSchema = Yup.object().shape({
  name: Yup.string().trim().min(2, "Mínimo 2 caracteres").max(50).required("Obrigatório"),
  color: Yup.string().required("Obrigatório"),
  position: Yup.number().integer().min(0),
});

const initialState = { name: "", color: "#7C3AED", kanban: false, position: 0 };

const TagModal = ({ open, onClose, tagId }) => {
  const classes = useStyles();
  const [tag, setTag] = useState(initialState);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (!tagId) {
      setTag(initialState);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get(`/tags/${tagId}`);
        setTag({
          name: data.name,
          color: data.color,
          kanban: data.kanban,
          position: data.position,
        });
      } catch (err) {
        toastError(err);
      }
    })();
  }, [tagId, open]);

  const handleSave = async (values) => {
    const payload = { ...values, position: Number(values.position) || 0 };
    try {
      if (tagId) {
        await api.put(`/tags/${tagId}`, payload);
      } else {
        await api.post("/tags", payload);
      }
      toast.success(i18n.t("tags.toasts.saved"));
      onClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>
        {i18n.t(tagId ? "tagModal.title.edit" : "tagModal.title.add")}
      </DialogTitle>
      <Formik
        initialValues={tag}
        enableReinitialize
        validationSchema={TagSchema}
        onSubmit={async (values, actions) => {
          await handleSave(values);
          actions.setSubmitting(false);
        }}
      >
        {({ touched, errors, isSubmitting, values, setFieldValue }) => (
          <Form>
            <DialogContent dividers>
              <Field
                as={TextField}
                label={i18n.t("tagModal.form.name")}
                name="name"
                autoFocus
                fullWidth
                variant="outlined"
                margin="dense"
                error={touched.name && Boolean(errors.name)}
                helperText={touched.name && errors.name}
              />
              <div className={classes.row}>
                <TextField
                  label={i18n.t("tagModal.form.color")}
                  value={values.color}
                  variant="outlined"
                  margin="dense"
                  onClick={() => setColorPickerOpen(true)}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <InputAdornment position="start">
                        <div
                          className={classes.colorAdornment}
                          style={{ backgroundColor: values.color }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <IconButton size="small" onClick={() => setColorPickerOpen(true)}>
                        <Colorize />
                      </IconButton>
                    ),
                  }}
                />
                <Field
                  as={TextField}
                  label={i18n.t("tagModal.form.position")}
                  name="position"
                  type="number"
                  variant="outlined"
                  margin="dense"
                  inputProps={{ min: 0 }}
                  error={touched.position && Boolean(errors.position)}
                />
              </div>
              <ColorPicker
                open={colorPickerOpen}
                currentColor={values.color}
                handleClose={() => setColorPickerOpen(false)}
                onChange={(color) => setFieldValue("color", color)}
              />
              <FormControlLabel
                control={
                  <Switch
                    color="primary"
                    checked={!!values.kanban}
                    onChange={(e) => setFieldValue("kanban", e.target.checked)}
                  />
                }
                label={i18n.t("tagModal.form.kanban")}
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={isSubmitting} variant="outlined">
                {i18n.t("tagModal.buttons.cancel")}
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting}
                className={classes.btnWrapper}
              >
                {i18n.t("tagModal.buttons.ok")}
                {isSubmitting && (
                  <CircularProgress size={24} className={classes.buttonProgress} />
                )}
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default TagModal;
